import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    CheckCircleOutlineRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    CloseRounded,
    DeleteOutlineRounded,
    EditRounded,
    EventAvailableRounded,
    FactoryRounded,
    RefreshRounded,
    SaveRounded,
    SearchRounded,
    UndoRounded,
    UploadFileRounded,
} from "@mui/icons-material";
import { Navigate } from "react-router-dom";
import EmbeddedLayout from "../../../components/admin/EmbeddedLayout";
import { baseURL } from "../../../config";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { useUser } from "../../../hooks/useUser";
import factoryDeliveryNoticesController, {
    IDeliveryNotice,
    IDeliveryNoticeFactory,
    IDeliveryNotices,
} from "../../../Api/factoryDeliveryNotices";

type FactoryDraft = {
    factoryName: string;
    image: File | null;
};

type NoticeDraft = {
    deliveryDates: string[];
    message: string;
};

type CalendarLog = {
    factory: IDeliveryNoticeFactory;
    notice: IDeliveryNotice;
};

type CalendarContextMenu = CalendarLog & {
    mouseX: number;
    mouseY: number;
};

const emptyFactoryDraft = (): FactoryDraft => ({
    factoryName: "",
    image: null,
});

const emptyNoticeDraft = (): NoticeDraft => ({
    deliveryDates: [],
    message: "",
});

const toDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const normalizeDateKey = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return toDateKey(date);
};

const getTodayDateKey = () => toDateKey(new Date());

const getNoticeDateKeys = (notice: IDeliveryNotice) => (
    (notice.deliveryDates || []).map(normalizeDateKey).filter(Boolean).sort()
);

const isNoticeVisible = (notice: IDeliveryNotice, todayKey: string) => (
    !notice.isCompleted && getNoticeDateKeys(notice).some((dateKey) => dateKey >= todayKey)
);

const isNoticeRelevant = (notice: IDeliveryNotice, todayKey: string) => (
    getNoticeDateKeys(notice).some((dateKey) => dateKey >= todayKey)
);

const formatDate = (value: string) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("pl-PL", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
};

const formatNoticeRange = (notice: IDeliveryNotice) => {
    const dates = (notice.deliveryDates || []).map(normalizeDateKey).filter(Boolean).sort();

    if (dates.length === 0) return "Brak dni";
    if (dates.length === 1) return formatDate(dates[0]);

    return `${formatDate(dates[0])} - ${formatDate(dates[dates.length - 1])}`;
};

const monthLabel = (date: Date) => new Intl.DateTimeFormat("pl-PL", {
    month: "long",
    year: "numeric",
}).format(date);

const getImageUrl = (image?: string) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    return `${baseURL}${image.replace(/\\/g, "/")}`;
};

const getMonthDays = (month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const firstDay = new Date(year, monthIndex, 1);
    const offset = (firstDay.getDay() + 6) % 7;
    const blanks = Array.from({ length: Math.min(offset, 6) }, () => null);
    const days = Array.from({ length: daysInMonth }, (_, index) => new Date(year, monthIndex, index + 1))
        .filter((date) => date.getDay() !== 0);

    return [...blanks, ...days];
};

const FactoryDeliveryNotices = ({ embedded = false }: { embedded?: boolean }) => {
    const user = useUser();
    const isAdmin = user.hasPermission('deliveryNotices.read');
    const canCompleteNotices = user.hasPermission('deliveryNotices.complete');
    const canEditNotices = user.hasPermission('deliveryNotices.update');
    const canDeleteNotices = user.hasPermission('deliveryNotices.delete');
    const authChecked = user.authReady;
    const { confirm, dialog: confirmDialog } = useConfirmDialog();

    const [config, setConfig] = useState<IDeliveryNotices | null>(null);
    const [month, setMonth] = useState(() => new Date());
    const [selectedFactoryId, setSelectedFactoryId] = useState("");
    const [isFactoryEditMode, setIsFactoryEditMode] = useState(false);
    const [isAddFactoryOpen, setIsAddFactoryOpen] = useState(false);
    const [factorySearchQuery, setFactorySearchQuery] = useState("");
    const [editedFactoryId, setEditedFactoryId] = useState("");
    const [factoryDraft, setFactoryDraft] = useState<FactoryDraft>(emptyFactoryDraft());
    const [isFactoryDialogOpen, setIsFactoryDialogOpen] = useState(false);
    const [noticeFactoryId, setNoticeFactoryId] = useState("");
    const [editedNoticeId, setEditedNoticeId] = useState("");
    const [noticeDraft, setNoticeDraft] = useState<NoticeDraft>(emptyNoticeDraft());
    const [isNoticeDialogOpen, setIsNoticeDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [factoryFileInputKey, setFactoryFileInputKey] = useState(0);
    const [factoryImagePreview, setFactoryImagePreview] = useState("");
    const [calendarContextMenu, setCalendarContextMenu] = useState<CalendarContextMenu | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const load = useCallback(async () => {
        if (!authChecked || !isAdmin) return;

        setIsLoading(true);
        setError(null);

        const res = await factoryDeliveryNoticesController.getDeliveryNotices();

        if (!res.ok || !res.deliveryNotices) {
            setConfig(null);
            setError(res.message || "Nie pobrano awizacji dostaw");
            setIsLoading(false);
            return;
        }

        setConfig(res.deliveryNotices);
        setIsLoading(false);
    }, [authChecked, isAdmin]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!factoryDraft.image) {
            setFactoryImagePreview("");
            return;
        }

        const previewUrl = URL.createObjectURL(factoryDraft.image);
        setFactoryImagePreview(previewUrl);

        return () => URL.revokeObjectURL(previewUrl);
    }, [factoryDraft.image]);

    const factories = useMemo(
        () => [...(config?.factories || [])].sort((a, b) => a.factoryName.localeCompare(b.factoryName, "pl")),
        [config?.factories]
    );

    const filteredFactories = useMemo(() => {
        const query = factorySearchQuery.trim().toLocaleLowerCase("pl");
        if (!query) return factories;

        return factories.filter((factory) => factory.factoryName.toLocaleLowerCase("pl").includes(query));
    }, [factories, factorySearchQuery]);

    const selectedFactory = useMemo(
        () => factories.find((factory) => factory._id === selectedFactoryId) || null,
        [factories, selectedFactoryId]
    );

    const noticeFactory = useMemo(
        () => factories.find((factory) => factory._id === noticeFactoryId) || null,
        [factories, noticeFactoryId]
    );

    const todayKey = useMemo(() => getTodayDateKey(), []);

    const monthDays = useMemo(() => getMonthDays(month), [month]);
    const visibleDateKeys = useMemo(
        () => new Set(monthDays.filter((date): date is Date => Boolean(date)).map(toDateKey)),
        [monthDays]
    );

    const selectedFactoryVisibleNotices = useMemo(
        () => (selectedFactory?.notices || []).filter((factoryNotice) => isNoticeRelevant(factoryNotice, todayKey)),
        [selectedFactory?.notices, todayKey]
    );

    const noticeFactoryVisibleNotices = useMemo(
        () => (noticeFactory?.notices || []).filter((factoryNotice) => isNoticeRelevant(factoryNotice, todayKey)),
        [noticeFactory?.notices, todayKey]
    );

    const calendarLogs = useMemo(() => {
        const logs = new Map<string, CalendarLog[]>();

        factories.forEach((factory) => {
            (factory.notices || []).forEach((factoryNotice) => {
                factoryNotice.deliveryDates?.forEach((dateValue) => {
                    const key = normalizeDateKey(dateValue);
                    if (!key || !visibleDateKeys.has(key)) return;
                    const list = logs.get(key) || [];
                    list.push({ factory, notice: factoryNotice });
                    logs.set(key, list);
                });
            });
        });

        return logs;
    }, [factories, visibleDateKeys]);

    const selectedDateSet = useMemo(() => new Set(noticeDraft.deliveryDates), [noticeDraft.deliveryDates]);

    if (authChecked && !isAdmin) {
        return <Navigate to="/logowanie" replace />;
    }

    const resetFactoryForm = () => {
        setFactoryDraft(emptyFactoryDraft());
        setFactoryFileInputKey((current) => current + 1);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const clearFactoryImage = () => {
        setFactoryDraft((current) => ({ ...current, image: null }));
        setFactoryFileInputKey((current) => current + 1);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const closeFactoryDialog = () => {
        setIsFactoryDialogOpen(false);
        setEditedFactoryId("");
        resetFactoryForm();
    };

    const openEditFactoryDialog = (factory: IDeliveryNoticeFactory) => {
        setEditedFactoryId(factory._id);
        setFactoryDraft({ factoryName: factory.factoryName, image: null });
        setIsFactoryDialogOpen(true);
        setError(null);
        setNotice(null);
    };

    const closeNoticeDialog = () => {
        setIsNoticeDialogOpen(false);
        setNoticeFactoryId("");
        setEditedNoticeId("");
        setNoticeDraft(emptyNoticeDraft());
    };

    const openNewNoticeDialog = (factory: IDeliveryNoticeFactory) => {
        setNoticeFactoryId(factory._id);
        setEditedNoticeId("");
        setNoticeDraft(emptyNoticeDraft());
        setIsNoticeDialogOpen(true);
        setError(null);
        setNotice(null);
    };

    const openEditNoticeDialog = (factory: IDeliveryNoticeFactory, factoryNotice: IDeliveryNotice) => {
        setNoticeFactoryId(factory._id);
        setEditedNoticeId(factoryNotice._id);
        setNoticeDraft({
            deliveryDates: (factoryNotice.deliveryDates || []).map(normalizeDateKey).filter(Boolean).sort(),
            message: factoryNotice.message || "",
        });
        setIsNoticeDialogOpen(true);
        setError(null);
        setNotice(null);
    };

    const toggleNoticeDate = (date: Date) => {
        const key = toDateKey(date);

        setNoticeDraft((current) => ({
            ...current,
            deliveryDates: current.deliveryDates.includes(key)
                ? current.deliveryDates.filter((item) => item !== key)
                : [...current.deliveryDates, key].sort(),
        }));
    };

    const changeMonth = (direction: -1 | 1) => {
        setMonth((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
    };

    const saveFactory = async () => {
        const factoryName = factoryDraft.factoryName.trim();

        if (!factoryName) {
            setError("Podaj nazwe fabryki");
            return;
        }

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const payload = {
            factoryName,
            image: factoryDraft.image,
        };
        const res = editedFactoryId
            ? await factoryDeliveryNoticesController.editFactory(editedFactoryId, payload)
            : await factoryDeliveryNoticesController.addFactory(payload);

        if (!res.ok || !res.deliveryNotices) {
            setError(res.message || "Nie zapisano fabryki");
            setIsSaving(false);
            return;
        }

        setConfig(res.deliveryNotices);
        if (editedFactoryId) {
            closeFactoryDialog();
        } else {
            resetFactoryForm();
        }
        setNotice(editedFactoryId ? "Zapisano fabrykę" : "Dodano fabrykę do bazy");
        setIsSaving(false);
    };

    const handleAddFactoryKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== "Enter" || isSaving || isLoading) return;
        event.preventDefault();
        saveFactory();
    };

    const selectFactory = (factory: IDeliveryNoticeFactory) => {
        setSelectedFactoryId(factory._id);
        openNewNoticeDialog(factory);
    };

    const saveNotice = async () => {
        if (!noticeFactory) return;

        if (noticeDraft.deliveryDates.length === 0) {
            setError("Wybierz przynajmniej jeden dzień awizacji");
            return;
        }

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const payload = {
            deliveryDates: noticeDraft.deliveryDates,
            message: noticeDraft.message.trim(),
        };
        const res = editedNoticeId
            ? await factoryDeliveryNoticesController.editNotice(noticeFactory._id, editedNoticeId, payload)
            : await factoryDeliveryNoticesController.addNotice(noticeFactory._id, payload);

        if (!res.ok || !res.deliveryNotices) {
            setError(res.message || "Nie zapisano awizacji");
            setIsSaving(false);
            return;
        }

        setConfig(res.deliveryNotices);
        setSelectedFactoryId(noticeFactory._id);
        closeNoticeDialog();
        setNotice("Zapisano awizację");
        setIsSaving(false);
    };

    const deleteNotice = async (factory: IDeliveryNoticeFactory, factoryNotice: IDeliveryNotice) => {
        const confirmed = await confirm(`Usunąć awizację fabryki "${factory.factoryName}"?`);
        if (!confirmed) return;

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await factoryDeliveryNoticesController.deleteNotice(factory._id, factoryNotice._id);

        if (!res.ok || !res.deliveryNotices) {
            setError(res.message || "Nie usunięto awizacji");
            setIsSaving(false);
            return;
        }

        setConfig(res.deliveryNotices);
        setNotice("Usunięto awizację");
        setIsSaving(false);
    };

    const confirmCompleteNotice = async (factory: IDeliveryNoticeFactory, factoryNotice: IDeliveryNotice, completed = true) => {
        const confirmed = await confirm(completed
            ? `Oznaczyć dostawę fabryki "${factory.factoryName}" jako dostarczoną? Awizacja zostanie skreślona, ale pozostanie widoczna.`
            : `Cofnąć oznaczenie dostawy fabryki "${factory.factoryName}"? Awizacja ponownie będzie aktywna.`);
        if (!confirmed) return;

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await factoryDeliveryNoticesController.completeNotice(factory._id, factoryNotice._id, completed);

        if (!res.ok || !res.deliveryNotices) {
            setError(res.message || (completed ? "Nie oznaczono dostawy jako dostarczonej" : "Nie cofnięto oznaczenia dostawy"));
            setIsSaving(false);
            return;
        }

        setConfig(res.deliveryNotices);
        setNotice(completed ? "Oznaczono dostawę jako dostarczoną" : "Cofnięto oznaczenie dostawy");
        setIsSaving(false);
    };

    const deleteFactory = async (factory: IDeliveryNoticeFactory) => {
        const confirmed = await confirm(`Usunąć fabrykę "${factory.factoryName}" z bazy awizacji?`);
        if (!confirmed) return;

        setIsSaving(true);
        setError(null);
        setNotice(null);

        const res = await factoryDeliveryNoticesController.deleteFactory(factory._id);

        if (!res.ok) {
            setError(res.message || "Nie usunięto fabryki");
            setIsSaving(false);
            return;
        }

        if (selectedFactoryId === factory._id) setSelectedFactoryId("");
        await load();
        setNotice("Usunięto fabrykę");
        setIsSaving(false);
    };

    const renderFactoryAvatar = (factory: IDeliveryNoticeFactory, size = 42) => {
        const imageUrl = getImageUrl(factory.image);

        return (
            <Avatar
                src={imageUrl}
                variant="rounded"
                sx={{
                    width: size,
                    height: size,
                    borderRadius: 1.25,
                    bgcolor: imageUrl ? "#fff" : "#eef2f7",
                    "& img": { objectFit: "contain", p: 0.35 },
                }}
            >
                <FactoryRounded fontSize="small" />
            </Avatar>
        );
    };

    return (
        <EmbeddedLayout embedded={embedded} title="Awizacje dostaw">
            <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fb", px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
                <Stack spacing={2} sx={{ maxWidth: 1480, mx: "auto" }}>
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                        <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" alignItems={{ lg: "center" }} gap={2}>
                            <Stack direction="row" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 52, height: 52, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#1d4ed8", color: "#fff" }}>
                                    <EventAvailableRounded />
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 950, color: "#0f172a", letterSpacing: 0 }}>
                                    Awizacje dostaw
                                </Typography>
                            </Stack>
                            <Stack direction="row" gap={1}>
                                <Tooltip title="Odśwież">
                                    <span>
                                        <IconButton onClick={load} disabled={isLoading || isSaving} sx={{ border: "1px solid #e2e8f0" }}>
                                            <RefreshRounded />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Paper>

                    {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
                    {notice ? <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert> : null}

                    <Stack direction={{ xs: "column", xl: "row" }} gap={2} alignItems="stretch">
                        <Paper
                            elevation={0}
                            sx={{
                                width: { xs: "100%", xl: 340 },
                                p: 1.5,
                                borderRadius: 2,
                                border: "1px solid #e5e7eb",
                                bgcolor: "#fbfdff",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <Stack spacing={1.5} sx={{ minHeight: 0, flex: 1 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                                    <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>Baza fabryk</Typography>
                                    <Stack direction="row" alignItems="center" gap={0.75}>
                                        <Stack direction="row" alignItems="center" gap={0.25}>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: isFactoryEditMode ? "#0f766e" : "#64748b" }}>
                                                Edycja
                                            </Typography>
                                            <Switch
                                                size="small"
                                                checked={isFactoryEditMode}
                                                onChange={(event) => setIsFactoryEditMode(event.target.checked)}
                                            />
                                        </Stack>
                                        <Tooltip title="Dodaj fabrykę">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    if (isAddFactoryOpen) {
                                                        resetFactoryForm();
                                                    }
                                                    setIsAddFactoryOpen((current) => !current);
                                                }}
                                                sx={{ border: "1px solid #dbe3ef", bgcolor: isAddFactoryOpen ? "#eef6f3" : "#fff" }}
                                            >
                                                {isAddFactoryOpen ? <CloseRounded fontSize="small" /> : <AddRounded fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>

                                <TextField
                                    label="Szukaj fabryki"
                                    size="small"
                                    value={factorySearchQuery}
                                    onChange={(event) => setFactorySearchQuery(event.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: <SearchRounded fontSize="small" sx={{ color: "#64748b", mr: 0.75 }} />,
                                    }}
                                    inputProps={{
                                        autoComplete: "off",
                                        autoCorrect: "off",
                                        autoCapitalize: "none",
                                        spellCheck: false,
                                    }}
                                />

                                {isAddFactoryOpen ? (
                                    <Paper elevation={0} onKeyDown={handleAddFactoryKeyDown} sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                                        <Stack spacing={1}>
                                            <TextField
                                                label="Nowa fabryka"
                                                size="small"
                                                value={factoryDraft.factoryName}
                                                onChange={(event) => setFactoryDraft((current) => ({ ...current, factoryName: event.target.value }))}
                                                fullWidth
                                            />
                                            <Button component="label" variant="outlined" size="small" startIcon={<UploadFileRounded />} sx={{ justifyContent: "flex-start", borderRadius: 1.5, fontWeight: 800 }}>
                                                {factoryDraft.image ? factoryDraft.image.name : "Zdjęcie"}
                                                <input
                                                    key={factoryFileInputKey}
                                                    ref={fileInputRef}
                                                    hidden
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(event) => setFactoryDraft((current) => ({ ...current, image: event.target.files?.[0] || null }))}
                                                />
                                            </Button>
                                            {factoryImagePreview ? (
                                                <Paper elevation={0} sx={{ p: 1, borderRadius: 1.5, border: "1px solid #e5e7eb", bgcolor: "#fff" }}>
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        <Avatar
                                                            src={factoryImagePreview}
                                                            variant="rounded"
                                                            sx={{
                                                                width: 56,
                                                                height: 56,
                                                                borderRadius: 1.5,
                                                                bgcolor: "#fff",
                                                                "& img": {
                                                                    objectFit: "contain",
                                                                    p: 0.5,
                                                                },
                                                            }}
                                                        >
                                                            <FactoryRounded />
                                                        </Avatar>
                                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                                            <Typography variant="caption" sx={{ display: "block", fontWeight: 800, color: "#64748b" }}>
                                                                Podgląd zdjęcia
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 800, color: "#0f172a" }}>
                                                                {factoryDraft.image?.name}
                                                            </Typography>
                                                        </Box>
                                                        <Tooltip title="Usuń zdjęcie">
                                                            <IconButton size="small" onClick={clearFactoryImage} sx={{ color: "#b91c1c" }}>
                                                                <CloseRounded fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </Paper>
                                            ) : null}
                                            <Button variant="contained" size="small" startIcon={<AddRounded />} onClick={saveFactory} disabled={isSaving || isLoading} sx={{ borderRadius: 1.5, fontWeight: 900 }}>
                                                Dodaj
                                            </Button>
                                        </Stack>
                                    </Paper>
                                ) : null}

                                <Divider />

                                <Stack spacing={0.75} sx={{ minHeight: 0, flex: 1, overflow: "auto", pr: 0.5, pt: 0.5 }}>
                                {factories.length === 0 && !isLoading ? (
                                    <Paper elevation={0} sx={{ p: 2, border: "1px dashed #cbd5e1", borderRadius: 1.5, textAlign: "center", bgcolor: "#f8fafc" }}>
                                        <Typography sx={{ fontWeight: 850 }}>Brak fabryk</Typography>
                                    </Paper>
                                ) : null}
                                {factories.length > 0 && filteredFactories.length === 0 ? (
                                    <Paper elevation={0} sx={{ p: 2, border: "1px dashed #cbd5e1", borderRadius: 1.5, textAlign: "center", bgcolor: "#f8fafc" }}>
                                        <Typography sx={{ fontWeight: 850 }}>Brak pasujących fabryk</Typography>
                                    </Paper>
                                ) : null}
                                {filteredFactories.map((factory) => {
                                    const activeNoticesCount = (factory.notices || []).filter((factoryNotice) => isNoticeVisible(factoryNotice, todayKey)).length;

                                    return (
                                        <Paper
                                            key={factory._id}
                                            elevation={0}
                                            onClick={() => selectFactory(factory)}
                                            sx={{
                                                p: 1.25,
                                                borderRadius: 1.5,
                                                border: selectedFactoryId === factory._id ? "1px solid #1d4ed8" : "1px solid #e5e7eb",
                                                cursor: "pointer",
                                                bgcolor: selectedFactoryId === factory._id ? "#eff6ff" : "#fff",
                                                "&:hover": { borderColor: "#1d4ed8", boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)" },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                                                {renderFactoryAvatar(factory)}
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography sx={{ fontWeight: 850, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {factory.factoryName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                                                        {activeNoticesCount} aktywnych
                                                    </Typography>
                                                </Box>
                                                {isFactoryEditMode ? (
                                                    <Stack direction="row" gap={0.25}>
                                                        <Tooltip title="Edytuj fabrykę">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    openEditFactoryDialog(factory);
                                                                }}
                                                            >
                                                                <EditRounded fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Usuń fabrykę">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    deleteFactory(factory);
                                                                }}
                                                                sx={{ color: "#b91c1c" }}
                                                            >
                                                                <DeleteOutlineRounded fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                ) : null}
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                                </Stack>
                            </Stack>
                        </Paper>

                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                            <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff", overflow: "hidden" }}>
                                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1} sx={{ p: 2 }}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <IconButton onClick={() => changeMonth(-1)} sx={{ border: "1px solid #e2e8f0" }}>
                                            <ChevronLeftRounded />
                                        </IconButton>
                                        <Typography sx={{ minWidth: 190, textAlign: "center", fontWeight: 950, color: "#0f172a", textTransform: "capitalize" }}>
                                            {monthLabel(month)}
                                        </Typography>
                                        <IconButton onClick={() => changeMonth(1)} sx={{ border: "1px solid #e2e8f0" }}>
                                            <ChevronRightRounded />
                                        </IconButton>
                                    </Stack>
                                    <Chip color="primary" variant="outlined" label="Widok pn-sb" />
                                </Stack>
                                <Divider />

                                <Box sx={{ p: { xs: 1, md: 2 } }}>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 1, mb: 1 }}>
                                        {["Pon", "Wt", "Sr", "Czw", "Pt", "Sob"].map((label) => (
                                            <Typography key={label} variant="caption" sx={{ textAlign: "center", fontWeight: 900, color: "#64748b" }}>
                                                {label}
                                            </Typography>
                                        ))}
                                    </Box>
                                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 1 }}>
                                        {monthDays.map((date, index) => {
                                            if (!date) {
                                                return <Box key={`blank-${index}`} sx={{ minHeight: 118 }} />;
                                            }

                                            const key = toDateKey(date);
                                            const logs = calendarLogs.get(key) || [];
                                            const isPast = key < todayKey;

                                            return (
                                                <Paper
                                                    key={key}
                                                    elevation={0}
                                                    sx={{
                                                        minHeight: 118,
                                                        p: 1,
                                                        borderRadius: 1.25,
                                                        border: logs.length ? (isPast ? "1px solid #cbd5e1" : "1px solid #93c5fd") : "1px solid #e5e7eb",
                                                        bgcolor: logs.length ? (isPast ? "#f8fafc" : "#eff6ff") : "#fff",
                                                        overflow: "visible",
                                                    }}
                                                >
                                                    <Typography sx={{ fontWeight: 950, color: isPast ? "#94a3b8" : "#0f172a", mb: 0.75 }}>
                                                        {date.getDate()}
                                                    </Typography>
                                                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                                        {logs.map((log) => {
                                                            const imageUrl = getImageUrl(log.factory.image);
                                                            const isMuted = isPast || log.notice.isCompleted;

                                                            return (
                                                                <Tooltip key={`${key}-${log.factory._id}-${log.notice._id}`} title={canCompleteNotices ? (log.notice.isCompleted ? "Cofnij oznaczenie" : "Oznacz jako dostarczoną") : (log.notice.isCompleted ? "Dostarczona" : "Awizacja zaplanowana")}>
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        gap={0.5}
                                                                        minWidth={0}
                                                                        onClick={() => {
                                                                            if (canCompleteNotices) confirmCompleteNotice(log.factory, log.notice, !log.notice.isCompleted);
                                                                        }}
                                                                        onContextMenu={(event) => {
                                                                            if (!canEditNotices && !canDeleteNotices) return;
                                                                            event.preventDefault();
                                                                            event.stopPropagation();
                                                                            setCalendarContextMenu({
                                                                                mouseX: event.clientX + 2,
                                                                                mouseY: event.clientY - 6,
                                                                                factory: log.factory,
                                                                                notice: log.notice,
                                                                            });
                                                                        }}
                                                                        aria-haspopup={canEditNotices || canDeleteNotices ? "menu" : undefined}
                                                                        sx={{
                                                                            cursor: canCompleteNotices ? "pointer" : canEditNotices || canDeleteNotices ? "context-menu" : "default",
                                                                            borderRadius: 1,
                                                                            px: 0.25,
                                                                            opacity: isMuted ? 0.62 : 1,
                                                                            textDecoration: log.notice.isCompleted ? "line-through" : "none",
                                                                            "&:hover": { bgcolor: canCompleteNotices ? "rgba(29, 78, 216, 0.08)" : "transparent" },
                                                                        }}
                                                                    >
                                                                        <Avatar
                                                                            src={imageUrl}
                                                                            sx={{
                                                                                width: 22,
                                                                                height: 22,
                                                                                bgcolor: imageUrl ? "#fff" : "#dbeafe",
                                                                                fontSize: 11,
                                                                                "& img": { objectFit: "contain", p: 0.15 },
                                                                            }}
                                                                        >
                                                                            {log.factory.factoryName.slice(0, 1)}
                                                                        </Avatar>
                                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: isMuted ? "#64748b" : "#1e3a8a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: log.notice.isCompleted ? "line-through" : "none" }}>
                                                                            {log.factory.factoryName}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Paper>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Paper>

                            <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fff", overflow: "hidden" }}>
                                {selectedFactory ? (
                                    <>
                                        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1.5} sx={{ p: 2 }}>
                                            <Stack direction="row" alignItems="center" gap={1.25} minWidth={0}>
                                                {renderFactoryAvatar(selectedFactory, 48)}
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontWeight: 950, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {selectedFactory.factoryName}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                                                        {selectedFactoryVisibleNotices.length} awizacji
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Stack direction="row" gap={1} flexWrap="wrap">
                                                {isFactoryEditMode ? (
                                                    <Button variant="outlined" startIcon={<EditRounded />} onClick={() => openEditFactoryDialog(selectedFactory)} disabled={isSaving}>
                                                        Edytuj fabrykę
                                                    </Button>
                                                ) : null}
                                                <Button variant="contained" startIcon={<AddRounded />} onClick={() => openNewNoticeDialog(selectedFactory)} disabled={isSaving}>
                                                    Dodaj awizację
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Divider />
                                        <Stack spacing={1} sx={{ p: 1.5 }}>
                                            {true ? (
                                                <Paper elevation={0} sx={{ p: 2, border: "1px dashed #cbd5e1", borderRadius: 1.5, textAlign: "center", bgcolor: "#f8fafc" }}>
                                                    <Typography sx={{ fontWeight: 850 }}>Aktualne awizacje tej fabryki są dostępne w oknie dodawania awizacji.</Typography>
                                                </Paper>
                                            ) : null}
                                            {([] as IDeliveryNotice[]).map((factoryNotice) => (
                                                <Paper
                                                    key={factoryNotice._id}
                                                    elevation={0}
                                                    sx={{ p: 1.5, borderRadius: 1.5, border: "1px solid #e5e7eb" }}
                                                >
                                                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                                                                {formatNoticeRange(factoryNotice)}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                                                                {(factoryNotice.deliveryDates || []).length} dni
                                                            </Typography>
                                                            {factoryNotice.message ? (
                                                                <Typography variant="body2" sx={{ color: "#334155", mt: 0.75 }}>
                                                                    {factoryNotice.message}
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                        <Stack direction="row" gap={0.5}>
                                                            {canCompleteNotices ? (
                                                                <Tooltip title={factoryNotice.isCompleted ? "Cofnij oznaczenie" : "Oznacz jako dostarczoną"}>
                                                                    <IconButton onClick={() => confirmCompleteNotice(selectedFactory, factoryNotice, !factoryNotice.isCompleted)} disabled={isSaving} sx={{ color: factoryNotice.isCompleted ? "#b45309" : "#15803d" }}>
                                                                        {factoryNotice.isCompleted ? <UndoRounded fontSize="small" /> : <CheckCircleOutlineRounded fontSize="small" />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : null}
                                                            <Tooltip title="Edytuj awizację">
                                                                <IconButton onClick={() => openEditNoticeDialog(selectedFactory, factoryNotice)} disabled={isSaving}>
                                                                    <EditRounded fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Usuń awizację">
                                                                <IconButton onClick={() => deleteNotice(selectedFactory, factoryNotice)} disabled={isSaving} sx={{ color: "#b91c1c" }}>
                                                                    <DeleteOutlineRounded fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: "center" }}>
                                        <FactoryRounded sx={{ fontSize: 42, color: "#64748b", mb: 1 }} />
                                        <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>
                                            Kliknij fabrykę, żeby dodać albo zobaczyć jej awizacje
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>

            <Dialog open={isFactoryDialogOpen} onClose={() => !isSaving && closeFactoryDialog()} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={{ fontWeight: 950 }}>
                            {editedFactoryId ? "Edytuj fabrykę" : "Nowa fabryka"}
                        </Typography>
                        <IconButton onClick={closeFactoryDialog} disabled={isSaving}>
                            <CloseRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <TextField
                            label="Nazwa fabryki"
                            value={factoryDraft.factoryName}
                            onChange={(event) => setFactoryDraft((current) => ({ ...current, factoryName: event.target.value }))}
                            fullWidth
                            autoFocus
                        />
                        <Button component="label" variant="outlined" startIcon={<UploadFileRounded />} sx={{ justifyContent: "flex-start", borderRadius: 1.5, fontWeight: 800 }}>
                            {factoryDraft.image ? factoryDraft.image.name : "Logo"}
                            <input
                                ref={fileInputRef}
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(event) => setFactoryDraft((current) => ({ ...current, image: event.target.files?.[0] || null }))}
                            />
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button variant="outlined" onClick={closeFactoryDialog} disabled={isSaving}>
                        Anuluj
                    </Button>
                    <Button variant="contained" startIcon={<SaveRounded />} onClick={saveFactory} disabled={isSaving} sx={{ fontWeight: 850 }}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isNoticeDialogOpen} onClose={() => !isSaving && closeNoticeDialog()} fullWidth maxWidth="md">
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={{ fontWeight: 950 }}>
                            {editedNoticeId ? "Edytuj awizację" : "Nowa awizacja"}: {noticeFactory?.factoryName || ""}
                        </Typography>
                        <IconButton onClick={closeNoticeDialog} disabled={isSaving}>
                            <CloseRounded />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label="Notatka"
                            value={noticeDraft.message}
                            onChange={(event) => setNoticeDraft((current) => ({ ...current, message: event.target.value }))}
                            multiline
                            minRows={2}
                            fullWidth
                        />

                        <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#f8fafc" }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <IconButton size="small" onClick={() => changeMonth(-1)} sx={{ border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
                                        <ChevronLeftRounded fontSize="small" />
                                    </IconButton>
                                    <Typography sx={{ fontWeight: 950, textTransform: "capitalize", minWidth: 170, textAlign: "center" }}>
                                        {monthLabel(month)}
                                    </Typography>
                                    <IconButton size="small" onClick={() => changeMonth(1)} sx={{ border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
                                        <ChevronRightRounded fontSize="small" />
                                    </IconButton>
                                </Stack>
                                <Chip size="small" label={`${noticeDraft.deliveryDates.length} dni`} />
                            </Stack>

                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 0.75, mb: 0.75 }}>
                                {["Pon", "Wt", "Sr", "Czw", "Pt", "Sob"].map((label) => (
                                    <Typography key={label} variant="caption" sx={{ textAlign: "center", fontWeight: 900, color: "#64748b" }}>
                                        {label}
                                    </Typography>
                                ))}
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 0.75 }}>
                                {monthDays.map((date, index) => {
                                    if (!date) return <Box key={`draft-blank-${index}`} sx={{ height: 44 }} />;

                                    const key = toDateKey(date);
                                    const selected = selectedDateSet.has(key);
                                    const isPastDate = key < todayKey;

                                    return (
                                        <Button
                                            key={`draft-${key}`}
                                            variant={selected ? "contained" : "outlined"}
                                            onClick={() => toggleNoticeDate(date)}
                                            disabled={isPastDate}
                                            sx={{
                                                height: 44,
                                                minWidth: 0,
                                                borderRadius: 1.25,
                                                fontWeight: 950,
                                                bgcolor: selected ? "#1d4ed8" : "#fff",
                                            }}
                                        >
                                            {date.getDate()}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Paper>

                        {noticeDraft.deliveryDates.length ? (
                            <Stack direction="row" gap={0.75} flexWrap="wrap">
                                {noticeDraft.deliveryDates.map((date) => (
                                    <Chip key={date} label={formatDate(date)} onDelete={() => setNoticeDraft((current) => ({ ...current, deliveryDates: current.deliveryDates.filter((item) => item !== date) }))} />
                                ))}
                            </Stack>
                        ) : null}

                        <Divider />

                        <Box>
                            <Typography sx={{ fontWeight: 950, color: "#0f172a", mb: 1 }}>
                                Obecne awizacje
                            </Typography>
                            <Stack spacing={1}>
                                {noticeFactoryVisibleNotices.length === 0 ? (
                                    <Paper elevation={0} sx={{ p: 1.5, border: "1px dashed #cbd5e1", borderRadius: 1.5, bgcolor: "#f8fafc", textAlign: "center" }}>
                                        <Typography sx={{ fontWeight: 850 }}>
                                            Ta fabryka nie ma awizacji do wyświetlenia
                                        </Typography>
                                    </Paper>
                                ) : null}
                                {noticeFactoryVisibleNotices.map((factoryNotice) => (
                                    <Paper
                                        key={factoryNotice._id}
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            border: factoryNotice.isCompleted ? "1px solid #bbf7d0" : "1px solid #e5e7eb",
                                            bgcolor: factoryNotice.isCompleted ? "#f0fdf4" : "#fff",
                                            opacity: factoryNotice.isCompleted ? 0.78 : 1,
                                        }}
                                    >
                                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 900, color: "#0f172a", textDecoration: factoryNotice.isCompleted ? "line-through" : "none" }}>
                                                    {formatNoticeRange(factoryNotice)}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "#64748b", textDecoration: factoryNotice.isCompleted ? "line-through" : "none" }}>
                                                    {(factoryNotice.deliveryDates || []).length} dni
                                                </Typography>
                                                {factoryNotice.message ? (
                                                    <Typography variant="body2" sx={{ color: "#334155", mt: 0.75, textDecoration: factoryNotice.isCompleted ? "line-through" : "none" }}>
                                                        {factoryNotice.message}
                                                    </Typography>
                                                ) : null}
                                                {factoryNotice.isCompleted ? (
                                                    <Chip size="small" color="success" variant="outlined" label="Dostarczona" sx={{ mt: 1 }} />
                                                ) : null}
                                            </Box>
                                            {noticeFactory ? (
                                                <Stack direction="row" gap={0.5}>
                                                    {canCompleteNotices ? (
                                                        <Tooltip title={factoryNotice.isCompleted ? "Cofnij oznaczenie" : "Oznacz jako dostarczoną"}>
                                                            <IconButton onClick={() => confirmCompleteNotice(noticeFactory, factoryNotice, !factoryNotice.isCompleted)} disabled={isSaving} sx={{ color: factoryNotice.isCompleted ? "#b45309" : "#15803d" }}>
                                                                {factoryNotice.isCompleted ? <UndoRounded fontSize="small" /> : <CheckCircleOutlineRounded fontSize="small" />}
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : null}
                                                    <Tooltip title="Edytuj awizację">
                                                        <IconButton onClick={() => openEditNoticeDialog(noticeFactory, factoryNotice)} disabled={isSaving}>
                                                            <EditRounded fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Usuń awizację">
                                                        <IconButton onClick={() => deleteNotice(noticeFactory, factoryNotice)} disabled={isSaving} sx={{ color: "#b91c1c" }}>
                                                            <DeleteOutlineRounded fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            ) : null}
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button variant="outlined" onClick={closeNoticeDialog} disabled={isSaving}>
                        Anuluj
                    </Button>
                    <Button variant="contained" startIcon={<SaveRounded />} onClick={saveNotice} disabled={isSaving} sx={{ fontWeight: 850 }}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>
            <Menu
                open={Boolean(calendarContextMenu)}
                onClose={() => setCalendarContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={calendarContextMenu ? { top: calendarContextMenu.mouseY, left: calendarContextMenu.mouseX } : undefined}
                slotProps={{ paper: { sx: { minWidth: 245, borderRadius: 2, boxShadow: "0 14px 36px rgba(15, 23, 42, 0.2)" } } }}
            >
                <MenuItem disabled sx={{ opacity: "1 !important", py: 1 }}>
                    <ListItemText
                        primary={calendarContextMenu?.factory.factoryName || "Awizacja"}
                        secondary={calendarContextMenu ? formatNoticeRange(calendarContextMenu.notice) : ""}
                        primaryTypographyProps={{ fontWeight: 950, color: "#0f172a" }}
                    />
                </MenuItem>
                <Divider />
                {canEditNotices ? (
                    <MenuItem onClick={() => {
                        if (!calendarContextMenu) return;
                        const { factory, notice: factoryNotice } = calendarContextMenu;
                        setCalendarContextMenu(null);
                        openEditNoticeDialog(factory, factoryNotice);
                    }}>
                        <ListItemIcon><EditRounded fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Edytuj awizację" />
                    </MenuItem>
                ) : null}
                {canDeleteNotices ? (
                    <MenuItem sx={{ color: "#b91c1c" }} onClick={() => {
                        if (!calendarContextMenu) return;
                        const { factory, notice: factoryNotice } = calendarContextMenu;
                        setCalendarContextMenu(null);
                        void deleteNotice(factory, factoryNotice);
                    }}>
                        <ListItemIcon sx={{ color: "inherit" }}><DeleteOutlineRounded fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Usuń awizację" />
                    </MenuItem>
                ) : null}
            </Menu>
            {confirmDialog}
        </EmbeddedLayout>
    );
};

export default FactoryDeliveryNotices;
